/**
 * RedirectIfAuthed -- Inverse of RequireAuth for auth pages.
 *
 * Wraps /login, /register, /forgot-password. If the user is already logged in,
 * sending them to a sign-in form is pointless — redirect them to /account
 * (the canonical browser landing).
 *
 * Usage as a layout-route element:
 *   <Route element={<RedirectIfAuthed />}>
 *     <Route path="/login" element={<LoginPage />} />
 *     <Route path="/register" element={<RegisterPage />} />
 *     <Route path="/forgot-password" element={<ForgotPasswordForm />} />
 *   </Route>
 *
 * Loading state shows a spinner — keeps the auth/login forms from flashing
 * before useAuth resolves.
 *
 * Issue #113 acceptance.
 */

import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'

interface RedirectIfAuthedProps {
  to?: string
}

export function RedirectIfAuthed({ to = '/account' }: RedirectIfAuthedProps) {
  const { isLoading, isLoggedIn } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center bg-ue-bg-darkest">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isLoggedIn) {
    return <Navigate to={to} replace />
  }

  return <Outlet />
}
